import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Camera, Images, Maximize2 } from 'lucide-react';
import PhotoSwipeLightbox from 'photoswipe/lightbox';
import 'photoswipe/style.css';
import '../styles/photoswipe-custom.css';
import LazyImage from '../components/LazyImage';
import Breadcrumb from '../components/Breadcrumb';
import './GalleryPage.css';

// Costante di modulo: l'effect di caricamento gira una volta sola (deps stabili).
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';

const GalleryPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [galleries, setGalleries] = useState<any[]>([]);
  const [allImages, setAllImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Carica le gallerie e le immagini dalle API
  useEffect(() => {
    const loadGalleries = async () => {
      try {
        setLoading(true);
        setError(null);

        // Carica le gallerie disponibili
        const galleriesResponse = await fetch(`${API_BASE_URL}/gallery/folders`);
        const galleriesData = await galleriesResponse.json();

        if (galleriesData.success) {
          setGalleries(galleriesData.galleries);

          // Carica tutte le immagini da tutte le gallerie
          const allImagesPromises = galleriesData.galleries.map(async (gallery: any) => {
            const imagesResponse = await fetch(`${API_BASE_URL}/gallery/folders/${gallery.slug}/images`);
            const imagesData = await imagesResponse.json();

            if (imagesData.success) {
              return imagesData.images.map((image: any) => ({
                ...image,
                src: image.url, // Le immagini sono servite dal frontend, non dal backend
                galleryName: gallery.name,
                gallerySlug: gallery.slug,
                category: gallery.name
              }));
            }
            return [];
          });

          const allImagesArrays = await Promise.all(allImagesPromises);
          const flatImages = allImagesArrays.flat();
          setAllImages(flatImages);
        } else {
          setError('Errore nel caricamento delle gallerie');
        }
      } catch (err) {
        console.error('Errore nel caricamento:', err);
        setError('Errore di connessione al server');
      } finally {
        setLoading(false);
      }
    };

    loadGalleries();
  }, []);

  // Calcola le categorie dinamicamente
  const categories = React.useMemo(() => {
    const categoryCount: { [key: string]: number } = {};

    allImages.forEach(image => {
      categoryCount[image.category] = (categoryCount[image.category] || 0) + 1;
    });

    return [
      { id: 'all', name: 'Tutte le foto', count: allImages.length },
      ...Object.entries(categoryCount).map(([category, count]) => ({
        id: category,
        name: category.charAt(0).toUpperCase() + category.slice(1),
        count
      }))
    ];
  }, [allImages]);

  // Filtra le immagini in base alla categoria selezionata
  const filteredImages = React.useMemo(() => {
    return selectedCategory === 'all'
      ? allImages
      : allImages.filter(img => img.category === selectedCategory);
  }, [allImages, selectedCategory]);

  // Inizializza PhotoSwipe
  useEffect(() => {
    if (filteredImages.length === 0) return;

    const lightbox = new PhotoSwipeLightbox({
      gallery: '#gallery-container',
      children: 'a',
      pswpModule: () => import('photoswipe'),

      // Opzioni visualizzazione
      bgOpacity: 0.95,
      spacing: 0.1,
      allowPanToNext: true,
      loop: true,

      // Zoom avanzato
      zoom: true,
      maxZoomLevel: 4,
      initialZoomLevel: 'fit',
      secondaryZoomLevel: 2,

      // Controlli
      pinchToClose: true,
      closeOnVerticalDrag: true,
      escKey: true,
      arrowKeys: true,
      returnFocus: true,
      clickToCloseNonZoomable: true,
      imageClickAction: 'zoom',
      tapAction: 'toggle-controls',
      doubleTapAction: 'zoom',

      // Animazioni
      showHideAnimationType: 'fade',
      showAnimationDuration: 333,
      hideAnimationDuration: 333,

      // Preload
      preload: [1, 3],
      preloaderDelay: 0,

      // UI Elements
      closeTitle: 'Chiudi (Esc)',
      zoomTitle: 'Zoom',
      arrowPrevTitle: 'Precedente',
      arrowNextTitle: 'Successiva',
      errorMsg: 'Impossibile caricare l\'immagine',
    });

    // Variabile per gestire lo slideshow
    let slideshowInterval: NodeJS.Timeout | null = null;
    let isSlideshow = false;

    // Aggiungi pulsanti personalizzati
    lightbox.on('uiRegister', function() {
      // Pulsante Slideshow
      lightbox.pswp?.ui?.registerElement({
        name: 'slideshow-button',
        order: 7,
        isButton: true,
        tagName: 'button',
        html: {
          isCustomSVG: true,
          inner: '<path d="M8 5v14l11-7z" id="pswp__icn-play"/>',
          outlineID: 'pswp__icn-play'
        },
        onClick: (event: any, el: any, pswp: any) => {
          if (!isSlideshow) {
            // Avvia slideshow
            isSlideshow = true;
            el.innerHTML = '<svg class="pswp__icn" viewBox="0 0 24 24"><path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" id="pswp__icn-pause"/></svg>';
            el.setAttribute('title', 'Pausa slideshow');

            slideshowInterval = setInterval(() => {
              if (pswp) {
                const currentIndex = pswp.currIndex;
                const totalSlides = pswp.getNumItems();

                if (currentIndex < totalSlides - 1) {
                  pswp.next();
                } else {
                  pswp.goTo(0); // Torna alla prima
                }
              }
            }, 3000); // 3 secondi
          } else {
            // Ferma slideshow
            isSlideshow = false;
            if (slideshowInterval) {
              clearInterval(slideshowInterval);
              slideshowInterval = null;
            }
            el.innerHTML = '<svg class="pswp__icn" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" id="pswp__icn-play"/></svg>';
            el.setAttribute('title', 'Avvia slideshow (3 sec)');
          }
        },
        onInit: (el: any) => {
          el.setAttribute('title', 'Avvia slideshow (3 sec)');
        }
      });

      // Pulsante Download
      lightbox.pswp?.ui?.registerElement({
        name: 'download-button',
        order: 8,
        isButton: true,
        tagName: 'a',
        html: {
          isCustomSVG: true,
          inner: '<path d="M20.5 14.3 17.1 18V10h-2.2v7.9l-3.4-3.6L10 16l6 6.1 6-6.1ZM23 23H9v2h14Z" id="pswp__icn-download"/>',
          outlineID: 'pswp__icn-download'
        },
        onInit: (el: any, pswp: any) => {
          el.setAttribute('download', '');
          el.setAttribute('target', '_blank');
          el.setAttribute('rel', 'noopener');
          el.setAttribute('title', 'Scarica immagine');

          pswp.on('change', () => {
            const currentSlide = pswp.currSlide;
            if (currentSlide?.data?.src) {
              el.href = currentSlide.data.src;
            }
          });
        }
      });

      // Pulsante Condividi
      lightbox.pswp?.ui?.registerElement({
        name: 'share-button',
        order: 9,
        isButton: true,
        tagName: 'button',
        html: {
          isCustomSVG: true,
          inner: '<path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z" id="pswp__icn-share"/>',
          outlineID: 'pswp__icn-share'
        },
        onClick: (event: any, el: any, pswp: any) => {
          const currentSlide = pswp.currSlide;
          if (currentSlide?.data?.src) {
            const shareData = {
              title: 'UNASCA Gallery',
              text: 'Guarda questa foto dalla galleria UNASCA',
              url: window.location.href
            };

            if (navigator.share) {
              navigator.share(shareData).catch(() => {
                // Fallback: copia link
                copyToClipboard(window.location.href);
              });
            } else {
              copyToClipboard(window.location.href);
            }
          }
        }
      });

      // Pulsante Info
      lightbox.pswp?.ui?.registerElement({
        name: 'info-button',
        order: 10,
        isButton: true,
        tagName: 'button',
        html: {
          isCustomSVG: true,
          inner: '<path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" id="pswp__icn-info"/>',
          outlineID: 'pswp__icn-info'
        },
        onClick: (event: any, el: any, pswp: any) => {
          const currentSlide = pswp.currSlide;
          const caption = currentSlide?.data?.element?.querySelector('img')?.alt || 'Nessuna descrizione';
          alert(`📷 ${caption}\n\n🔍 Usa la rotella del mouse o pinch per zoomare\n⌨️ Usa le frecce per navigare\n💾 Clicca il pulsante download per salvare`);
        }
      });
    });

    // Helper per copiare negli appunti
    const copyToClipboard = (text: string) => {
      navigator.clipboard.writeText(text).then(() => {
        alert('✅ Link copiato negli appunti!');
      }).catch(() => {
        alert('❌ Impossibile copiare il link');
      });
    };

    // Ferma slideshow quando si chiude la galleria
    lightbox.on('close', () => {
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
        slideshowInterval = null;
        isSlideshow = false;
      }
    });

    lightbox.init();

    return () => {
      // Cleanup: ferma slideshow e distruggi lightbox
      if (slideshowInterval) {
        clearInterval(slideshowInterval);
      }
      lightbox.destroy();
    };
  }, [filteredImages]);

  // Foto per i nastri della testata: due file che scorrono in versi opposti
  const nastri = (() => {
    const scelte = allImages.slice(0, 24);
    if (scelte.length < 6) return [];
    const meta = Math.ceil(scelte.length / 2);
    return [scelte.slice(0, meta), scelte.slice(meta)].filter((r) => r.length > 0);
  })();

  const formatoData = (data?: string) => {
    if (!data) return '';
    const d = new Date(data);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });
  };

  const testata = (
    <section className="relative isolate overflow-hidden bg-primary-950">
      {nastri.length > 0 && (
        <div className="absolute inset-0 -z-10 flex -rotate-6 scale-110 flex-col justify-center gap-4 opacity-60" aria-hidden="true">
          {nastri.map((riga, r) => (
            <div key={r} className={`nastro-foto flex gap-4 ${r % 2 ? 'nastro-foto--inverso' : ''}`}>
              {[...riga, ...riga].map((img, i) => (
                <img
                  key={`${img.id}-${i}`}
                  src={img.src}
                  alt=""
                  decoding="async"
                  className="h-36 w-56 flex-shrink-0 rounded-xl object-cover md:h-44 md:w-64"
                />
              ))}
            </div>
          ))}
        </div>
      )}
      <div className="absolute inset-0 -z-10 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/30" />

      <div className="container-custom flex min-h-[28rem] items-center py-20 md:py-24">
        <div className="max-w-2xl motion-safe:animate-fade-in">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 ring-1 ring-white/20 backdrop-blur-sm">
            <Camera className="h-4 w-4 text-primary-300" aria-hidden="true" />
            <span className="text-sm font-semibold text-white">Momenti da ricordare</span>
          </div>
          <h1 className="mb-6 text-4xl md:text-6xl font-bold tracking-tight text-white">
            Galleria
          </h1>
          <p className="mb-8 text-base md:text-xl leading-relaxed text-white/80">
            Momenti significativi, eventi e attività dell'associazione catturati nel tempo
          </p>
          {!loading && !error && (
            <dl className="flex gap-3">
              {[
                { valore: galleries.length, etichetta: 'Album' },
                { valore: allImages.length, etichetta: 'Foto' },
              ].map(({ valore, etichetta }) => (
                <div key={etichetta} className="flex min-w-[7rem] flex-col-reverse rounded-2xl bg-white/10 px-5 py-3 ring-1 ring-white/15 backdrop-blur-sm">
                  <dt className="text-sm text-white/75">{etichetta}</dt>
                  <dd className="text-2xl md:text-3xl font-bold text-white tabular-nums">{valore}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      </div>

      <div className="pointer-events-none absolute bottom-10 right-10 hidden -rotate-6 text-right text-white lg:block xl:right-16" aria-hidden="true">
        <span className="block text-4xl leading-tight drop-shadow-lg" style={{ fontFamily: "'Caveat', cursive" }}>
          Ogni foto,<br />una storia
        </span>
        <svg viewBox="0 0 220 16" className="ml-auto mt-1 w-44 text-primary-400" fill="none">
          <path d="M3 12 C 60 2, 140 2, 217 8" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
        </svg>
      </div>
    </section>
  );

  const cornice = (contenuto: React.ReactNode) => (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <Helmet>
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link href="https://fonts.googleapis.com/css2?family=Caveat:wght@600&display=swap" rel="stylesheet" />
      </Helmet>
      {testata}
      <div className="container-custom pt-6">
        <Breadcrumb
          items={[
            { label: 'Home', href: '/' },
            { label: 'Galleria', current: true }
          ]}
        />
      </div>
      {contenuto}
    </div>
  );

  if (loading) {
    return cornice(
      <div className="container-custom py-12" aria-busy="true">
        <p className="mb-6 text-sm text-gray-600 dark:text-gray-400">Caricamento galleria...</p>
        <div className="columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4">
          {[64, 48, 56, 40, 60, 44, 52, 36].map((h, i) => (
            <div
              key={i}
              className="mb-4 break-inside-avoid animate-pulse rounded-2xl bg-gray-200 dark:bg-gray-800"
              style={{ height: `${h * 4}px` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return cornice(
      <div className="container-custom py-20">
        <div className="mx-auto max-w-md rounded-2xl bg-white p-8 text-center shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
          <h2 className="mb-4 text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Errore nel caricamento
          </h2>
          <p className="mb-8 text-base text-gray-600 dark:text-gray-400">
            {error}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="btn btn-primary"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return cornice(
    <>
      {/* Album */}
      {galleries.length > 0 && (
        <section className="pt-10 pb-4" aria-labelledby="titolo-album">
          <div className="container-custom">
            <div className="mb-5 flex items-end justify-between gap-4">
              <h2 id="titolo-album" className="text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                Album
              </h2>
              <span className="text-sm text-gray-500 dark:text-gray-400">Scorri per vederli tutti</span>
            </div>
          </div>
          <div className="container-custom">
            <ul className="-mx-6 flex snap-x snap-mandatory scroll-px-6 sm:scroll-px-8 lg:scroll-px-12 gap-5 overflow-x-auto px-6 pb-6 sm:-mx-8 sm:px-8 lg:-mx-12 lg:px-12">
              {galleries.map((album: any) => (
                <li key={album.slug} className="w-72 flex-shrink-0 snap-start">
                  <Link
                    to={`/gallery/${album.slug}`}
                    className="group block overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-black/5 transition-shadow hover:shadow-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:bg-gray-900 dark:ring-white/10"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden bg-gray-200 dark:bg-gray-800">
                      {album.coverImage ? (
                        <img
                          src={album.coverImage}
                          alt=""
                          loading="lazy"
                          className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-105"
                        />
                      ) : (
                        <div className="flex h-full items-center justify-center">
                          <Images className="h-10 w-10 text-gray-400" aria-hidden="true" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                      <span className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-gray-900">
                        <Images className="h-3.5 w-3.5" aria-hidden="true" />
                        {album.imageCount} foto
                      </span>
                      <div className="absolute inset-x-0 bottom-0 p-4 text-white">
                        <h3 className="text-base font-bold leading-snug">{album.name}</h3>
                        {formatoData(album.date) && (
                          <p className="mt-0.5 text-xs capitalize text-white/80">{formatoData(album.date)}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between px-4 py-3 text-sm font-semibold text-primary-700 dark:text-primary-400">
                      Apri l'album
                      <ArrowRight className="h-4 w-4 transition-transform motion-safe:group-hover:translate-x-1" aria-hidden="true" />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Tutte le foto */}
      <section className="container-custom pb-20 pt-6">
        <div className="mb-8">
          <h2 className="mb-5 text-2xl md:text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Categorie
          </h2>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Filtra le foto per categoria">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                aria-pressed={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-950 ${
                  selectedCategory === category.id
                    ? 'bg-primary-700 text-white shadow'
                    : 'bg-white text-gray-700 ring-1 ring-gray-200 hover:bg-primary-50 hover:text-primary-800 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-800'
                }`}
              >
                {category.name} <span className="text-xs opacity-70">({category.count})</span>
              </button>
            ))}
          </div>
        </div>

        {/* Muro di foto a colonne, con PhotoSwipe */}
        <div
          id="gallery-container"
          className="columns-1 gap-4 sm:columns-2 lg:columns-3 xl:columns-4"
        >
          {filteredImages.map((image) => (
            <a
              key={image.id}
              href={image.src}
              data-pswp-width={image.width || 1920}
              data-pswp-height={image.height || 1080}
              target="_blank"
              rel="noreferrer"
              className="group relative mb-4 block break-inside-avoid overflow-hidden rounded-2xl bg-gray-200 shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 dark:bg-gray-800 dark:focus-visible:ring-offset-gray-950 motion-safe:animate-fade-in"
              style={{ aspectRatio: image.width && image.height ? `${image.width} / ${image.height}` : '4 / 3' }}
            >
              <LazyImage
                src={image.src}
                alt={image.alt}
                className="h-full w-full object-cover transition-transform duration-500 motion-safe:group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/0 to-black/0 opacity-0 transition-opacity duration-300 group-hover:opacity-100 group-focus-visible:opacity-100" />
              <div className="absolute inset-x-0 bottom-0 translate-y-2 p-4 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100">
                <p className="truncate text-sm font-medium text-white">
                  {image.alt}
                </p>
                <p className="mt-0.5 text-xs text-white/75">
                  Categoria: {image.category}
                </p>
              </div>
              <span className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-900 opacity-0 shadow transition-opacity duration-300 group-hover:opacity-100" aria-hidden="true">
                <Maximize2 className="h-4 w-4" />
              </span>
            </a>
          ))}
        </div>

        {filteredImages.length === 0 && (
          <div className="rounded-2xl bg-white py-12 text-center shadow-sm ring-1 ring-black/5 dark:bg-gray-900 dark:ring-white/10">
            <p className="text-gray-500 dark:text-gray-400">
              Nessuna foto trovata per questa categoria.
            </p>
          </div>
        )}
      </section>
    </>
  );
};

export default GalleryPage;
